import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Briefcase, Users, TrendingUp, Plus } from 'lucide-react';
import { useAuth } from '../../context/AuthContext';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:5000';

const Dashboard: React.FC = () => {
  const { token } = useAuth();
  const [stats, setStats] = useState({
    totalJobs: 0,
    activeJobs: 0,
    totalApplicants: 0,
    recentApplicants: 0,
  });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchStats = async () => {
      try {
        const [jobsRes, applicantsRes] = await Promise.all([
          fetch(`${BACKEND_URL}/jobs/all`, {
            headers: { 'Authorization': `Bearer ${token}` }
          }),
          fetch(`${BACKEND_URL}/applicants`, {
            headers: { 'Authorization': `Bearer ${token}` }
          }),
        ]);

        const jobs = await jobsRes.json();
        const applicants = await applicantsRes.json();

        const activeJobs = jobs.filter((job: any) => job.isActive).length;
        
        // Count applicants from last 7 days
        const sevenDaysAgo = new Date();
        sevenDaysAgo.setDate(sevenDaysAgo.getDate() - 7);
        const recentApplicants = applicants.filter(
          (app: any) => new Date(app.createdAt) > sevenDaysAgo
        ).length;

        setStats({
          totalJobs: jobs.length,
          activeJobs,
          totalApplicants: applicants.length,
          recentApplicants,
        });
      } catch (error) {
        console.error('Failed to fetch stats:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchStats();
  }, []);

  const statCards = [
    {
      title: 'Total Job Postings',
      value: stats.totalJobs,
      icon: Briefcase,
      gradient: 'linear-gradient(135deg, var(--pw-secondary) 0%, var(--pw-secondary-light) 100%)',
      link: '/admin/jobs',
    },
    {
      title: 'Active Jobs',
      value: stats.activeJobs,
      icon: TrendingUp,
      gradient: 'linear-gradient(135deg, var(--pw-primary) 0%, var(--pw-primary-light) 100%)',
      link: '/admin/jobs',
    },
    {
      title: 'Total Applicants',
      value: stats.totalApplicants,
      icon: Users,
      gradient: 'linear-gradient(135deg, var(--pw-accent) 0%, var(--pw-accent-light) 100%)',
      link: '/admin/applicants',
    },
    {
      title: 'Recent Applicants',
      value: stats.recentApplicants,
      subtitle: 'Last 7 days',
      icon: Users,
      gradient: 'linear-gradient(135deg, var(--pw-success) 0%, var(--pw-success-light) 100%)',
      link: '/admin/applicants',
    },
  ];

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div 
          style={{ 
            color: 'var(--pw-muted-light)',
            fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, sans-serif"
          }}
        >
          Loading dashboard...
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-8" style={{ fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, sans-serif" }}>
      {/* Stats Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        {statCards.map((stat, index) => {
          const Icon = stat.icon;
          return (
            <Link
              key={index}
              to={stat.link}
              className="group rounded-2xl p-6 transition-all duration-300 hover:scale-[1.02] hover:shadow-2xl"
              style={{ 
                background: 'var(--pw-gradient-card)',
                border: '1px solid var(--pw-surface-tertiary)',
                boxShadow: '0 4px 24px rgba(0, 0, 0, 0.3)',
                backdropFilter: 'blur(10px)'
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.borderColor = 'var(--pw-primary)';
                e.currentTarget.style.boxShadow = '0 8px 32px rgba(124, 58, 237, 0.2)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.borderColor = 'var(--pw-surface-tertiary)';
                e.currentTarget.style.boxShadow = '0 4px 24px rgba(0, 0, 0, 0.3)';
              }}
            >
              <div className="flex items-center justify-between mb-4">
                <div 
                  className="w-14 h-14 rounded-xl flex items-center justify-center shadow-lg group-hover:scale-110 transition-transform duration-300"
                  style={{ background: stat.gradient }}
                >
                  <Icon className="w-7 h-7 text-white" />
                </div>
              </div>
              <h3 
                className="text-sm font-medium mb-2 uppercase tracking-wide" 
                style={{ 
                  color: 'var(--pw-muted-light)',
                  fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, sans-serif"
                }}
              >
                {stat.title}
              </h3>
              <p 
                className="text-4xl font-bold mb-1" 
                style={{ 
                  color: 'var(--pw-text-main)',
                  fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, sans-serif",
                  letterSpacing: '-0.02em'
                }}
              >
                {stat.value}
              </p>
              {stat.subtitle && (
                <p 
                  className="text-xs mt-2" 
                  style={{ 
                    color: 'var(--pw-muted-light)',
                    fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, sans-serif"
                  }}
                >
                  {stat.subtitle}
                </p>
              )}
            </Link>
          );
        })}
      </div>

      {/* Quick Actions */}
      <div 
        className="rounded-2xl p-8 backdrop-blur-sm"
        style={{ 
          background: 'var(--pw-gradient-card)',
          border: '1px solid var(--pw-surface-tertiary)',
          boxShadow: '0 4px 24px rgba(0, 0, 0, 0.3)',
          backdropFilter: 'blur(10px)'
        }}
      >
        <h3 
          className="text-xl font-bold mb-6" 
          style={{ 
            color: 'var(--pw-text-main)',
            fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, sans-serif",
            letterSpacing: '-0.01em'
          }}
        >
          Quick Actions
        </h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <Link
            to="/admin/jobs/create"
            className="flex items-center gap-3 px-6 py-4 rounded-xl transition-all duration-200 font-medium shadow-lg"
            style={{ 
              background: 'linear-gradient(135deg, var(--pw-primary) 0%, var(--pw-primary-light) 100%)',
              color: 'var(--pw-text-main)',
              fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, sans-serif"
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.opacity = '0.9';
              e.currentTarget.style.transform = 'translateY(-2px)';
              e.currentTarget.style.boxShadow = '0 8px 24px rgba(124, 58, 237, 0.4)';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.opacity = '1';
              e.currentTarget.style.transform = 'translateY(0)';
              e.currentTarget.style.boxShadow = '0 4px 12px rgba(124, 58, 237, 0.3)';
            }}
          >
            <Plus className="w-5 h-5" />
            <span>Create New Job Posting</span>
          </Link>
          <Link
            to="/admin/applicants"
            className="flex items-center gap-3 px-6 py-4 rounded-xl transition-all duration-200 font-medium"
            style={{ 
              backgroundColor: 'var(--pw-surface-tertiary)',
              color: 'var(--pw-text-main)',
              border: '1px solid var(--pw-surface-secondary)',
              fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, sans-serif"
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.backgroundColor = 'var(--pw-primary)';
              e.currentTarget.style.transform = 'translateY(-2px)';
              e.currentTarget.style.boxShadow = '0 4px 12px rgba(124, 58, 237, 0.2)';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.backgroundColor = 'var(--pw-surface-tertiary)';
              e.currentTarget.style.transform = 'translateY(0)';
              e.currentTarget.style.boxShadow = 'none';
            }}
          >
            <Users className="w-5 h-5" />
            <span>View All Applicants</span>
          </Link>
        </div>
      </div>

      {/* Welcome Message */}
      <div 
        className="rounded-2xl p-10 relative overflow-hidden"
        style={{ 
          background: 'linear-gradient(135deg, var(--pw-primary) 0%, var(--pw-primary-light) 100%)',
          boxShadow: '0 8px 32px rgba(124, 58, 237, 0.3)'
        }}
      >
        <div className="absolute inset-0 opacity-10" style={{ backgroundImage: 'radial-gradient(circle at 20% 50%, white 0%, transparent 50%), radial-gradient(circle at 80% 80%, white 0%, transparent 50%)' }}></div>
        <div className="relative z-10">
          <h2 
            className="text-3xl font-bold mb-3 text-white" 
            style={{ 
              fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, sans-serif",
              letterSpacing: '-0.02em'
            }}
          >
            Welcome to PattrnWorks Admin
          </h2>
          <p 
            className="text-white opacity-90 text-lg" 
            style={{ 
              fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, sans-serif"
            }}
          >
            Manage your job postings, review applicants, and keep your careers page up to date.
          </p>
        </div>
      </div>
    </div>
  );
};

export default Dashboard;

